import { memo, useEffect } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import {
  atResolution,
  estimateCredits,
  hasResolutions,
  resolutionOf,
  resolutionsOf,
  shapesFor,
  sizeFor,
  type PictureModel,
  type PictureSize,
} from '../../shared/services/pictureService'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The card that makes pictures
 * -------------------------------------------------------------
 * Its own file, and not one of the two in `nodes.tsx`, because it is not the
 * same kind of thing. An image node and a skill node are labels with ports;
 * this has a form in it, a price on its button, and a number of ports that
 * changes as the board is wired.
 *
 * EVERYTHING VISIBLE, NOTHING BEHIND A DROPDOWN
 *
 * It began as five dropdowns. Opening a folded card and then opening a menu
 * inside it to change one setting is two gestures to see what a single glance
 * should have told you — and on a board, where the card sits next to the
 * pictures it made, the settings ARE the label. They are chips now: what is
 * chosen is readable without touching anything, and changing it is one click.
 *
 * RATIO IS ONE LIST AGAIN, AND EVERY TILE IS REAL
 *
 * It was one dropdown mixing shape and resolution; then it was two rows,
 * shape and resolution, multiplied together. The second version was tidier and
 * wrong: 16:9 at 1K computes to 1024x576, and the API refuses that as below
 * its minimum pixel budget. Where that floor sits was never established — a
 * probe said all eleven sizes were fine including the one known to fail, which
 * was the method breaking rather than an answer.
 *
 * So the tiles are the model's own list and nothing else. A list cannot
 * express a combination nobody offers; a calculation always can.
 */

export interface PictureNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string

  /** Open unless folded by hand (false). Unfolded it is four times the height of a folded card. */
  open?: boolean

  prompt?: string
  model?: string
  /** Real pixels from the model's own list — '1024x1024' — or 'auto'. */
  size?: string
  quality?: string
  n?: number

  /**
   * The runs this card has in flight, by id.
   *
   * A list and not a flag. A flag meant one card could be making one picture,
   * and the second click did nothing at all — no queue, no second set of
   * frames, just a button that had gone grey. Several at once is the whole
   * reason the work went onto a queue.
   */
  runs?: string[]
  error?: string
  /** What the last run actually cost. Only ever the real figure. */
  credits?: number
  madeCount?: number

  /* handed in by the canvas, never stored */
  models?: PictureModel[]
  /**
   * How many runs this PERSON has going, anywhere, and the ceiling.
   *
   * Not this card's own count. The board, the skills page and the Miro panel
   * all spend from one account, so five here and five there would be ten — and
   * the thing being limited is the spending, not the card.
   */
  live?: number
  maxLive?: number
  /** How many reference pictures are wired in right now. */
  connected?: number
  onEdit?: (patch: Partial<PictureNodeData>) => void
  onRun?: () => void
}

/**
 * How many reference ports to draw.
 *
 * Three to begin with, because a card with one port looks like it takes one
 * picture, and then always one spare beyond whatever is wired — so there is
 * somewhere to drop the next one without first having to ask for a port. It
 * stops at whatever the model accepts, because a port that cannot be used is a
 * promise the run will refuse.
 */
function portCount(connected: number, limit: number): number {
  return Math.min(Math.max(3, connected + 1), Math.max(1, limit))
}

/** One row of chips. The shape every setting on this card takes. */
function Chips<T extends string | number>({
  label,
  value,
  options,
  disabled,
  columns,
  onPick,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  disabled?: boolean
  /**
   * Lay the chips out in a grid of this many, rather than letting them wrap.
   *
   * For the rows that do not fit on one line. Wrapping leaves a full line and
   * then a stub — ten numbers came out as seven and three — which reads as an
   * accident rather than as a set. Equal columns give two full rows of five,
   * and each chip gets wider in the bargain, which is what makes a number
   * inside a racetrack look like a racetrack rather than like a dot.
   */
  columns?: number
  onPick: (value: T) => void
}) {
  if (options.length === 0) return null
  return (
    <div className={`pnode__field${disabled ? ' pnode__field--off' : ''}`}>
      <span className="pnode__label">{label}</span>
      <div
        className={`pnode__chips${columns ? ' pnode__chips--grid' : ''}`}
        style={columns ? { gridTemplateColumns: `repeat(${columns}, 1fr)` } : undefined}
      >
        {options.map((one) => (
          <button
            key={String(one.value)}
            type="button"
            /* nodrag, or picking a setting drags the card out from under the
               pointer — React Flow treats a press anywhere on a node as the
               start of a drag unless told otherwise. */
            className={`pnode__chip nodrag${one.value === value ? ' pnode__chip--on' : ''}`}
            disabled={disabled}
            onClick={() => onPick(one.value)}
          >
            {one.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * The ratio tiles: a rectangle of the right shape, and its name underneath.
 *
 * A picture's shape is the one setting on this card that a word describes
 * badly and a drawing describes instantly. "2:3" has to be read and worked
 * out; a tall rectangle does not.
 */
function Shapes({
  value,
  options,
  onPick,
}: {
  value: string
  options: PictureSize[]
  onPick: (value: string) => void
}) {
  if (options.length === 0) return null
  return (
    <div className="pnode__field">
      <span className="pnode__label">Ratio</span>
      {/* Five across past twelve ratios: three rows either way (see the page). */}
      <div className={`pnode__tiles${options.length > 12 ? ' pnode__tiles--five' : ''}`}>
        {options.map((one) => {
          // Drawn to the longer edge, so a 1:1 and a 16:9 share an outline
          // rather than one of them being mysteriously smaller.
          const long = Math.max(one.w, one.h) || 1
          const style =
            one.w > 0
              ? { width: `${Math.max(5, (one.w / long) * 26)}px`, height: `${Math.max(5, (one.h / long) * 26)}px` }
              : undefined
          return (
            <button
              key={one.value}
              type="button"
              className={`pnode__tile nodrag${one.value === value ? ' pnode__tile--on' : ''}`}
              onClick={() => onPick(one.value)}
              title={one.value === 'auto' ? 'The model chooses' : one.value}
            >
              <span
                className={`pnode__shape${one.w > 0 ? '' : ' pnode__shape--auto'}`}
                style={style}
                aria-hidden="true"
              />
              <span className="pnode__tilename">{one.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export const PictureNode = memo(function PictureNode({ id, data, selected }: NodeProps) {
  const card = data as PictureNodeData
  // Open unless somebody folded it (Huanrui, 2026-10-06). A card saved before
  // this, with no answer either way, opens too.
  const isOpen = card.open !== false
  const remeasure = useUpdateNodeInternals()

  const models = card.models ?? []
  const model = models.find((one) => one.slug === card.model) ?? models[0]

  // What this model can actually make: a card saved on another model, or one
  // switched to Nano Banana (which has no `auto`), shows and sends a real size.
  const size = sizeFor(model, card.size ?? '')
  const quality = card.quality ?? 'auto'
  const count = Math.min(Math.max(1, card.n ?? 1), model?.maxImages ?? 1)
  const prompt = card.prompt ?? ''

  const wired = card.connected ?? 0
  const ports = portCount(wired, model?.maxReferences ?? 8)
  const mine = (card.runs ?? []).length
  const live = card.live ?? mine
  const full = live >= (card.maxLive ?? 5)

  // A port that appears or disappears moves every other port on the card, and
  // React Flow measures where they are once. Without this the wires stay where
  // the ports were — the same defect the image nodes had before their pictures
  // had loaded, in a different costume.
  useEffect(() => {
    remeasure(id)
  }, [id, ports, isOpen, remeasure])

  const estimate = model ? estimateCredits(model, quality, size, count, wired) : 0
  const edit = (patch: Partial<PictureNodeData>) => card.onEdit?.(patch)

  return (
    <div
      className={[
        'cnode',
        'pnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        // The same white glow every other card gets when something is wired
        // into it. It was missing here because the rule had been written as
        // "if this is a skill node" rather than "if this is a card that does
        // work" — so a new kind of card inherited none of it.
        wired > 0 ? 'cnode--lit' : '',
        mine > 0 ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      {/* Numbered down the side, because the order is the point: somebody
          writes "the label from the second picture" meaning the second one. */}
      {Array.from({ length: ports }, (_, index) => {
        const top = ((index + 1) / (ports + 1)) * 100
        return (
          <Handle
            key={`ref-${index}`}
            type="target"
            id={`ref-${index}`}
            position={Position.Left}
            className="cnode__port pnode__port"
            style={{ top: `${top}%` }}
          >
            <span className="pnode__portnum">{index + 1}</span>
          </Handle>
        )
      })}
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      {/*
        The name is a label and the toggle is a button, and they are not the
        same element any more.
        
        The whole header used to be the button, so the title was a control —
        which meant every attempt to read the card, point at it, or pick it up
        by its name folded or unfolded it instead. A control should be the size
        of its own intent: the plus is small, in the corner, and nowhere near
        anything somebody would click for another reason.
      */}
      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <textarea
            /* nowheel so a long prompt scrolls instead of zooming the board,
               nodrag so selecting text does not drag the card. Both are React
               Flow's own class names. They replace a stopPropagation on the
               whole card body, which had quietly taken the wheel away from the
               board everywhere over the card — the zoom stopped working and
               nothing said why. */
            className="pnode__prompt nodrag nowheel"
            value={prompt}
            placeholder="What should it make?"
            rows={6}
            onChange={(event) => edit({ prompt: event.target.value })}
            /* Otherwise every keystroke that happens to be a shortcut on the
               board — delete, c, v — does what it does on the board instead of
               what it does in a text box. */
            onKeyDown={(event) => event.stopPropagation()}
          />

          <Chips
            label="Model"
            value={model?.slug ?? ''}
            options={models.map((one) => ({ value: one.slug, label: one.label }))}
            onPick={(slug) => {
              // Shapes, resolutions and qualities belong to a model, so
              // switching can strand a setting the new one does not have.
              // Cleared here rather than left to fail at the far end.
              const next = models.find((one) => one.slug === slug)
              edit({
                model: slug,
                size: sizeFor(next, size),
                quality: next?.qualities.some((one) => one.value === quality) ? quality : 'auto',
                n: Math.min(count, next?.maxImages ?? 1),
              })
            }}
          />

          {/* Directly under the model, because it is the setting that moves
              the price most: `high` is about sixty-five times `low`. */}
          <Chips
            label="Quality"
            value={quality}
            options={(model?.qualities ?? []).map((one) => ({
              value: one.value,
              label: one.label,
            }))}
            onPick={(value) => edit({ quality: value })}
          />

          {/* Nano Banana: resolution and ratio, no quality -- the quality
              chips above hide themselves when a model lists none. */}
          {hasResolutions(model) && model ? (
            <Chips
              label="Resolution"
              value={resolutionOf(model, size)}
              options={resolutionsOf(model).map((one) => ({ value: one, label: one }))}
              onPick={(resolution) => edit({ size: atResolution(model, size, resolution) })}
            />
          ) : null}

          <Shapes
            value={size}
            options={shapesFor(model, size)}
            onPick={(value) => edit({ size: value })}
          />

          <Chips
            label="Quantity"
            value={count}
            options={Array.from({ length: model?.maxImages ?? 1 }, (_, index) => ({
              value: index + 1,
              label: String(index + 1),
            }))}
            columns={5}
            onPick={(value) => edit({ n: value })}
          />

          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {/*
        Only while the card is open — and it is the one control that is, which
        is the point.
        
        Folded, the card is a title bar the width of a thumbnail, usually sitting
        among the pictures it made. A button that spends money should not be the
        largest thing on it, reachable by a stray click at a moment when nothing
        else on the card can even be read. Unfolding first is one extra gesture
        and it is the gesture that shows you what you are about to buy.
        
        Except while it is running, when the button becomes the only place the
        news is: a folded card with a run in flight would otherwise look exactly
        like a folded card doing nothing.
      */}
      {isOpen || mine > 0 ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={() => card.onRun?.()}
          /* Running is no longer a reason to refuse — pressing it again starts
             another. Only the cap is, and it says so rather than going quietly
             grey, because a disabled button with no explanation reads as a
             fault rather than as a limit. */
          disabled={full || !prompt.trim() || !model}
          title={
            full
              ? `${live} already going, here or elsewhere — wait for one to finish`
              : prompt.trim()
                ? 'Make the pictures'
                : 'Write a prompt first'
          }
        >
          {full ? (
            'Queued — ' + live + ' already going'
          ) : (
            <>
              Run
              {/* "about", and always: the real number is counted by whatever
                  made the picture and does not exist until afterwards. */}
              {estimate > 0 ? (
                <span className="pnode__credits">about {estimate} credits</span>
              ) : null}
              {mine > 0 ? <span className="pnode__credits">· {mine} going</span> : null}
            </>
          )}
        </button>
      ) : null}

      {mine === 0 && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
